import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import {
  Home,
  Explore,
  Saved,
  CreatePost,
  Profile,
  EditPost,
  PostDetails,
  UpdateProfile,
  ChatContainer,
  Bizz,
  
} from "@/_root/pages";
import AuthLayout from "./_auth/AuthLayout";
import RootLayout from "./_root/RootLayout";
import SignupForm from "@/_auth/forms/SignupForm";
import SigninForm from "@/_auth/forms/SigninForm";
import { Toaster } from "@/components/ui/toaster";
import { Loader } from "@/components/shared";

import "./globals.css";
import ForgotPassword from "./_auth/forms/ForgotPassword";
import ResetPassword from "./_auth/forms/ResetPassword";
import VerifyPhone from "./_auth/forms/VerifyPhone";
import { AuthCallback } from "./_auth/forms/AuthCallback";
import PrivacyPolicy from "./_auth/forms/PrivacyPolicy";
import Admin from "./_root/pages/AdminPage";
import { useUserContext } from "./context/AuthContext";
import { HelmetProvider } from 'react-helmet-async';
import Transactions from "./_root/pages/Transactions";
import { useEffect } from "react";
import { notificationService } from "./services/NotificationService";
import { useGetCurrentUser } from "./lib/react-query/queries";
import { TermsAndConditions } from "./_auth/forms/TermsAndConditions";
import LivePage from "./_root/pages/live";
import CreateStreamPage from "./_root/pages/live/create";
import StreamViewPage from "./_root/pages/live/[id]";
import FAQ from "./_auth/forms/FAQ";
import TrustScoreDashboard from "./components/shared/TrustScoreDashboard";
import UserGuide from "./_auth/forms/UserGuide";
import DataDeletion from "./_auth/forms/DataDeletion";
import Landing from "./_root/pages/Landing";
import Dealers from "./_root/pages/Dealers";
import { buildSignInRedirect } from "@/lib/auth/redirect";
import SEOComponent from "@/components/shared/SEOComponent";
import { getLandingBootState } from "@/lib/seo/landing-boot.mjs";
import { canAccessPostModeration } from "@/lib/adminAccess";

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, isLoading } = useUserContext();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex-center w-full h-screen bg-dark-1">
        <div className="flex-center flex-col gap-4">
          <img
            src="/assets/images/busiconn.svg"
            alt="BUSICONN"
            className="w-32 h-auto"
          />
          <Loader />
          <p className="text-light-3 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={buildSignInRedirect(`${location.pathname}${location.search}`)} replace />;
  }

  return children;
};

const RequirePostModerationAccess = ({ children }: { children: JSX.Element }) => {
  const { user, isLoading } = useUserContext();

  if (isLoading) return <Loader />;
  return canAccessPostModeration(user) ? children : <Navigate to="/home" replace />;
};

const App = () => {
  const { isAuthenticated, isLoading } = useUserContext();
  const location = useLocation();
  const { showDuringAuth } = getLandingBootState(location.pathname);
  const { data: currentUser } = useGetCurrentUser();
  
  useEffect(() => {
    // Mobile sound pre-load
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const handleInteraction = () => {
      new Audio("/assets/sounds/notification.mp3").load();
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
    };

    if (isMobile) {
      window.addEventListener("click", handleInteraction, { once: true });
      window.addEventListener("touchstart", handleInteraction, { once: true });
    }

    // Notification listener
    if (currentUser?.$id) {
      const unsubscribe = notificationService.listenForUserNotifications(currentUser.$id);
      return () => unsubscribe();
    }
  }, [currentUser?.$id]);

  if (isLoading && !showDuringAuth) {
    return (
      <HelmetProvider>
        <SEOComponent metadataOnly />
        <div className="flex-center w-full h-screen bg-dark-1">
          <div className="flex-center flex-col gap-4">
            <img
              src="/assets/images/busiconn.svg"
              alt="BUSICONN"
              className="w-32 h-auto"
            />
            <Loader />
            <p className="text-light-3 text-sm">Loading...</p>
          </div>
        </div>
      </HelmetProvider>
    );
  }
  
  return (
    <HelmetProvider>
      <SEOComponent metadataOnly />
      <main className="flex min-h-[100dvh] flex-col">
        <Routes>
          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/sign-in" element={<SigninForm />} />
            <Route path="/sign-up" element={<SignupForm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-phone" element={<VerifyPhone />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/termsandconditions" element={<TermsAndConditions />} />
            <Route path="/busiconn-user-guide" element={<UserGuide />} />
          </Route>

          {/* Root index route - redirect based on auth status */}
          <Route 
            index 
            element={isAuthenticated ? <Navigate to="/home" replace /> : <Landing />} 
          />

          {/* Main app routes */}
          <Route element={<RootLayout />}>
            {/* Public routes (accessible without authentication) */}
            <Route path="/home" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/live" element={<LivePage />} />
            <Route path="/live/:id" element={<StreamViewPage />} />
            <Route path="/dealers" element={<Dealers />} />
            <Route path="/posts/:id" element={<PostDetails />} />
            <Route path="/profile/:id/*" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/privacy-policies" element={<PrivacyPolicy />} />
            <Route path="/termandcondition" element={<TermsAndConditions />} />
            <Route path="/busiconn-guide" element={<UserGuide />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Private routes (require authentication) */}
            <Route path="/saved" element={<RequireAuth><Saved /></RequireAuth>} />
            <Route path="/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
            <Route path="/chats" element={<RequireAuth><ChatContainer /></RequireAuth>} />
            <Route path="/chats/:id" element={<RequireAuth><ChatContainer /></RequireAuth>} />
            <Route path="/create-post" element={<RequireAuth><CreatePost /></RequireAuth>} />
            <Route path="/live/create" element={<RequireAuth><CreateStreamPage /></RequireAuth>} />
            <Route path="/update-post/:id" element={<RequireAuth><EditPost /></RequireAuth>} />
            <Route path="/update-profile/:id" element={<RequireAuth><UpdateProfile /></RequireAuth>} />
            <Route
              path="/admin-moderation"
              element={
                <RequireAuth>
                  <RequirePostModerationAccess><Admin /></RequirePostModerationAccess>
                </RequireAuth>
              }
            />
            <Route path="/bizz" element={<RequireAuth><Bizz /></RequireAuth>} />
            <Route path="/trust-score" element={<RequireAuth><TrustScoreDashboard /></RequireAuth>} />
            
            {/* Catch-all route */}
            <Route path="*" element={<Home />} />
          </Route>
        </Routes>

        <Toaster />
      </main>
    </HelmetProvider>
  );
};

export default App;
